import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { asShortLink, editionPrefix } from 'src/app/app.component';
import {
  copyTextToClipboard,
  getPinWithEdition,
  INFO_DIALOG_WIDTH,
} from 'src/app/utility/app.utils';
import { QrData, QrModalComponent } from 'src/app/widget/qr-model.component';
import { QrService } from 'src/app/widget/qr.service';
import {
  EmbedDialogComponent,
  EmbedDialogData,
} from '../../panel/embed-dialog.component';
import {
  HelpVideoDialogComponent,
  HelpVideoDialogData,
} from '../help-video-dialog.component';
import { UtilityService } from 'src/app/utility/utility.service';
import { NgScrollbar } from 'ngx-scrollbar';
import { NgTemplateOutlet } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { ButtonComponent } from 'src/app/shared/components/button/button.component';
import {
  ShowInfoDialogComponent,
  ShowInfoItem,
} from '../show-info-dialog/show-info-dialog.component';
import { ButtonLinkComponent } from '../button-link/button-link.component';
import { QrCodePreviewComponent } from 'src/app/widget/qr-code-preview/qr-code-preview.component';

@Component({
  selector: 'qa-share-section',
  templateUrl: './share-section.component.html',
  styleUrls: ['./share-section.component.scss'],
  imports: [
    NgScrollbar,
    NgTemplateOutlet,
    MatIcon,
    TranslateModule,
    MatTooltip,
    ButtonComponent,
    ButtonLinkComponent,
    QrCodePreviewComponent,
  ],
})
export class ShareSectionComponent implements OnInit {
  @Output() inviteAction = new EventEmitter();
  @Output() generateFlyer = new EventEmitter();
  @Output() closeShareInfo = new EventEmitter();
  shortLinkPrefix = asShortLink('', false);
  asShortLink = asShortLink;
  @Input() pinCode: string;
  @Input() openInNewTabText: string;
  @Input() generatingFlyer: boolean;
  @Input() canDownLoadFlyer: boolean;
  @Input() embedDialogData: EmbedDialogData;
  @Input() helpVideoDialogData: HelpVideoDialogData;
  @Input() hasTogglePanel: boolean = false;
  @Input() hideAlert: boolean = true;
  @Input() isScheduledExam: boolean;
  @Input() safeExamBrowser: boolean;
  @Output() toggleSection = new EventEmitter<void>();
  qrSvg: SafeHtml;
  qrDataUrl: string;

  supportsClipboard = !!navigator.clipboard;

  editionPrefix = editionPrefix();

  constructor(
    private translator: TranslateService,
    private matSnackBar: MatSnackBar,
    private sanitizer: DomSanitizer,
    private matDialog: MatDialog,
    private qr: QrService,
    private utilityService: UtilityService
  ) {}

  async ngOnInit() {
    await this.initialize(this.pinCode);
  }

  async initialize(pin: string) {
    const qrSVG = await this.qr.getSVG(asShortLink(pin));
    this.qrSvg = this.sanitizer.bypassSecurityTrustHtml(qrSVG);
    this.qr
      .getDataURL('' + `${asShortLink(pin)}`)
      .then((qrDataUrl) => (this.qrDataUrl = qrDataUrl));
  }

  copyWebLink(copySafeLink?: boolean) {
    try {
      copyTextToClipboard(asShortLink(this.pinCode, true, true, copySafeLink));
      this.matSnackBar.open(
        this.translator.instant('exam.message.copiedToClipboard')
      );
    } catch (error) {
      this.matSnackBar.open(
        this.translator.instant('exam.message.notCopiedToClipboard')
      );
    }
  }
  copyPin() {
    this.utilityService.copyPin(getPinWithEdition(this.pinCode));
  }

  openQrModel() {
    const data: QrData = {
      qr: this.qrSvg,
      pin: this.pinCode,
      qrDataUrl: this.qrDataUrl,
      openInNewTabText: this.openInNewTabText,
      canDownLoadFlyer: this.canDownLoadFlyer,
      generatingFlyer: this.generatingFlyer,
      embedDialogData: this.embedDialogData,
      invitationLink: {
        click: () => this.inviteAction.emit(),
      },
      generateFlyer: {
        click: () => this.generateFlyer.emit(),
      },
    };
    this.matDialog.open(QrModalComponent, {
      data,
      backdropClass: 'blur-backdrop',
    });
  }

  embedCourse() {
    this.matDialog.open<unknown, EmbedDialogData>(EmbedDialogComponent, {
      data: this.embedDialogData as EmbedDialogData,
      autoFocus: false,
    });
  }

  openVideo() {
    this.matDialog.open<HelpVideoDialogComponent, HelpVideoDialogData>(
      HelpVideoDialogComponent,
      {
        data: this.helpVideoDialogData,
      }
    );
  }

  openSafeExamBrowserInfoDialog() {
    this.matDialog.open<ShowInfoDialogComponent, ShowInfoItem>(
      ShowInfoDialogComponent,
      {
        data: {
          infoData: this.translator.instant('exam.tooltip.safeExamBrowser'),
          chipData: {
            label: this.translator.instant(
              'dashboardNew.examCard.academicButton'
            ),
            link: 'https://quizacademy.de/akademie/examen/#examen-sicher-durchfuehren',
            icon: 'school',
          },
        },
        autoFocus: false,
        width: INFO_DIALOG_WIDTH,
        height: 'auto',
        panelClass: 'tooltip-modal',
      }
    );
  }
}
